/* eslint-disable no-underscore-dangle */
import { api } from './index';
import { slugify } from 'garden-utils/index';
import type {
  PostDocument,
  galleriesSort,
  IUser,
  WithCreatorData,
  GalleryDocument,
  FPost
} from 'garden-common';

import type { GalleryFiltersSchema } from './feed.util';


export type GalleryVisibility = 'everyone' | 'followers' | 'talents' | 'private';

export async function createGallery(
  creatorId: string,
  visible: GalleryVisibility,
  title: string,
  description?: string,
  collaborators?: IUser<string>['_id'][],
  posts?: PostDocument['_id'][]
) {
  const res = await api.post('/v2/gallery/create', {
    id: creatorId,
    visible,
    title,
    description,
    collaborators,
    posts
  });

  return res.data.gallery as GalleryDocument;
}

export type MyGalleriesSingleResponse = Omit<GalleryDocument, 'creator' | 'talents' | 'posts'> & {
  creator: { profileImage: string; userName: string; };
  talents: { profileImage: string; userName: string; }[];
  collaborators: { profileImage: string; userName: string; }[];
  posts: FPost<string>[];
  selected: boolean;
};

export async function getMyGalleries(includeCollaboratorGalleries = true) {
  const res = await api.post('/v2/gallery/me', {
    params: { includeCollaborator: includeCollaboratorGalleries }
  });
  return res.data.galleries as MyGalleriesSingleResponse[];
}

export async function addPostToGalleries(
  postId: PostDocument['_id'],
  galleryIds: GalleryDocument['_id'][]
) {
  console.log(postId, galleryIds);
  if (!postId || galleryIds.length <= 0) {
    throw new Error('Post ID and Gallery IDs must be included');
  }

  const res = await api.put('/v2/gallery/post/add', { postId, galleryIds });
  return res;
}
export async function removePostFromGallery(
  postId: PostDocument['_id'],
  galleryId: GalleryDocument['_id']
) {
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-assignment
  const res = await api.put(`/v2/gallery/remove/${galleryId}`, { postId });
  return res;
}

export async function addItemsToGalleries(
  items: { _id: string; type: 'post' | 'talent'; }[],
  galleryIds: GalleryDocument['_id'][]
) {
  const res = await api.put('/v2/gallery/add', { items, galleryIds });

  return res;
}

export async function getGalleries(options: {
  limit: number;
  page: number;
  collaboratorOrCreator: string;
  sort: (typeof galleriesSort)[number];
  filters: GalleryFiltersSchema;
}): Promise<GalleryDocument[]> {
  const res = await api.post('/v2/gallery/all', {
    ...options
  });

  return res.data.galleries;
}

export type GalleryDetails = {
  _id: string;
  title: string;
  description: string;
  collaborators: {
    _id: IUser<string>['_id'];
    profileImage: IUser<string>['profileImage'];
    userName: IUser<string>['userName'];
    name: IUser<string>['name'];
  }[];
  creator: {
    _id: IUser<string>['_id'];
    profileImage: IUser<string>['profileImage'];
    userName: IUser<string>['userName'];
    name: IUser<string>['name'];
  };
  posts: string[];
  items: (FPost<string> & Partial<WithCreatorData>)[] | IUser<string>[];
  visible: 'everyone' | 'followers' | 'talents' | 'private';
  talents: IUser<string>[];
  followers: IUser<string>['_id'][];
  coverImageUrl: string;
  subcategories: string[];
  createdAt: Date;
  updatedAt: Date;
};

export async function getGallery(title: string): Promise<GalleryDetails> {
  try {
    const response = await api.post(`v2/gallery?slug=${slugify(title)}`);
    if (!response) throw new Error('No response when fetching gallery details');

    const galleryData = response?.data as { gallery: GalleryDetails; };
    const gallery = galleryData?.gallery;

    return gallery;

  } catch (e: unknown) {
    if (e instanceof Error) console.log(`Server Error: ${e.message}`);
    if (typeof e === 'string') console.log(`Server Error: ${e}`);
  }
}

export async function editGallery(gallery: Partial<GalleryDocument>) {
  if (!gallery._id) throw new Error('Gallery ID must be included');

  const res = await api.patch('v2/gallery/edit/' + String(gallery._id), {
    updates: gallery
  });
  return res.data.gallery;
}

export async function followGallery(galleryId: string, userId: string) {
  const res = await api.put('v2/gallery/follow/' + galleryId, { userId });
  return res;
}

export async function unfollowGallery(galleryId: string, userId: string) {
  const res = await api.put('v2/gallery/unfollow/' + galleryId, { userId });
  return res;
}
